import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import MatterBreadcrumbsMedSummaryAsset from '@/matter/components/matter-breadcrumbs/MatterBreadcrumbsMedSummaryAsset'
import { Button } from '@/components/button/Button'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { ConflictModal } from '@/user_files/drive/components/ConflictModal'
import * as Popover from '@radix-ui/react-popover'
import { Upload, InfoCircle, ShieldCheck } from '@/assets/icons'
import { brandedAIName } from '@/util/enterprise'
import { matterPaths } from '@/routing/paths'
import { useUserFiles } from '@/user_files/files/hooks/useUserFiles'
import { selectAllFiles, selectUploadFilesByServerFileId } from '@/uppy-file-upload/store/selectors'
import MedSummaryFileCard from './MedSummaryFileCard'
import { getMedSummaryAssetFileCardState } from '../../utils/get-med-summary-asset-file-card-state'
import { useMedSummaryAssetFilesUpload } from '../../hooks/useMedSummaryAssetFilesUpload'
import { postPreviewAddFiles } from '../../fetch/post-preview-add-files'
import { MedSummaryAssetActions } from '../../store/slice'
import {
  selectInitializeError,
  selectInitializePending,
  selectIsUpdateActive,
  selectMedSummaryAssetForMatter,
  selectMutationError,
  selectMutationPending,
} from '../../store/selectors'
import { FileAddDisposition, FileAddPreviewItem } from '../../store/schemas'
import { useOpenMedSummarySource } from '@/medical-billing-summaries/sources/useOpenMedSummarySource'
import { MedSummarySourceProvider } from '@/medical-billing-summaries/sources/schemas'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { trackMedSummaryEvent } from '@/analytics/utils/medsummary-analytics'
import { AddFilesMatterFilesDialog } from './AddFilesMatterFilesDialog'
import { StagedFileCard } from './StagedFileCard'
import { dispositionDisplay } from './disposition-display'
import { IncludedInSummaryDrawer } from './IncludedInSummaryDrawer'

const kPreviewDebounceMs = 400

/**
 * Add Files: stage uploads and matter files, preview their dispositions against the
 * living billing summary (net-new extraction only), then generate or update it.
 */
export default function AddFilesPage() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const activeMatterId = useAppSelector((state: RootState) => state.mattersState.activeMatterId)
  const matterId = activeMatterId ?? undefined
  const asset = useAppSelector((state: RootState) => selectMedSummaryAssetForMatter(state, { matterId }))
  const isUpdateActive = useAppSelector((state: RootState) => selectIsUpdateActive(state, { matterId }))
  const staging = useAppSelector((state: RootState) => state.medSummaryAssetState.addFilesStaging)
  const uploadFilesByClientTmpId = useAppSelector(selectAllFiles)
  const uploadFilesByServerFileId = useAppSelector(selectUploadFilesByServerFileId)

  const initializePending = useAppSelector((state: RootState) => selectInitializePending(state, { matterId }))
  const initializeError = useAppSelector((state: RootState) => selectInitializeError(state, { matterId }))
  const mutationPending = useAppSelector((state: RootState) => selectMutationPending(state, { assetId: asset?.id }))
  const mutationError = useAppSelector((state: RootState) => selectMutationError(state, { assetId: asset?.id }))

  const submitPending = asset ? mutationPending : initializePending
  const submitError = asset ? mutationError : initializeError

  const [isMatterFilesDialogOpen, setIsMatterFilesDialogOpen] = useState(false)
  const [previewItemsByFileId, setPreviewItemsByFileId] = useState<Record<string, FileAddPreviewItem>>({})

  const stagedUserFileIds = staging.stagedUserFileIds
  const { filesById: stagedUserFilesById, error: stagedUserFilesLoadError } = useUserFiles(stagedUserFileIds)

  const upload = useMedSummaryAssetFilesUpload({ enabled: true, matterId: activeMatterId })
  const { openMedSummarySource } = useOpenMedSummarySource()

  // Reset staging when entering for a different matter
  useEffect(() => {
    if (activeMatterId && staging.matterId !== activeMatterId) {
      dispatch(MedSummaryAssetActions.resetAddFilesStaging({ matterId: activeMatterId }))
    }
  }, [dispatch, activeMatterId, staging.matterId])

  // Fingerprint identity changes when the staged set OR any staged file's processing status
  // changes, so the preview re-runs when an upload finishes processing (not just when the id
  // list changes) - otherwise a NOT_READY disposition goes stale and keeps blocking the CTA
  // after the file completes. Staged ids are derived from the fingerprint so the effect can
  // depend on a single stable value (user_file ids are UUIDs and carry no ':').
  const stagedReadinessFingerprint = stagedUserFileIds
    .map((fileId) => {
      const uploadState = uploadFilesByServerFileId[fileId]
      const file = uploadState?.backend?.record ?? stagedUserFilesById[fileId]
      return `${fileId}:${file?.processing_status ?? 'unknown'}`
    })
    .join(',')
  const stagedPreviewIds = useMemo(
    () => (stagedReadinessFingerprint ? stagedReadinessFingerprint.split(',').map((entry) => entry.split(':')[0]) : []),
    [stagedReadinessFingerprint]
  )

  // Debounced disposition preview against the existing billing summary
  useEffect(() => {
    if (!asset || stagedPreviewIds.length === 0) {
      setPreviewItemsByFileId({})
      return
    }
    const assetId = asset.id
    const timeout = setTimeout(() => {
      postPreviewAddFiles(assetId, { user_file_ids: stagedPreviewIds })
        .then((response) => {
          const byId: Record<string, FileAddPreviewItem> = {}
          for (const item of response.items) byId[item.user_file_id] = item
          setPreviewItemsByFileId(byId)
        })
        .catch((error) => {
          console.error('Error previewing add files:', error)
        })
    }, kPreviewDebounceMs)
    return () => clearTimeout(timeout)
  }, [asset, stagedPreviewIds])

  // Navigate back to the summary when a submit lands: on success the new/updated summary shows;
  // on a FIRST-generation failure (no existing summary) the blank state shows the dismissible error
  // banner. Update failures stay here with the inline mutation error.
  const wasPendingRef = useRef(false)
  const submittedRef = useRef(false)
  useEffect(() => {
    if (submittedRef.current && wasPendingRef.current && !submitPending && matterId && (!submitError || !asset)) {
      submittedRef.current = false
      navigate(matterPaths.billing.index(matterId))
    }
    wasPendingRef.current = submitPending
  }, [submitPending, submitError, asset, matterId, navigate])

  const stagedCards = stagedUserFileIds.map((fileId) => {
    const uploadState = uploadFilesByServerFileId[fileId]
    const file = uploadState?.backend?.record ?? stagedUserFilesById[fileId]
    const cardState = getMedSummaryAssetFileCardState({ fileId, file, uploadState, loadError: stagedUserFilesLoadError && !file })
    // With an existing asset the disposition comes from the server preview (diff vs the summary).
    // On a first-time generation there's no asset to diff against, so preview-add never runs — every
    // ready file is implicitly NEW. Surface that locally so the "Ready to process" line still shows.
    const preview: FileAddPreviewItem | undefined = asset
      ? previewItemsByFileId[fileId]
      : cardState.ready
      ? { user_file_id: fileId, display_name: cardState.fileName, disposition: FileAddDisposition.NEW, possible_duplicate_of: null }
      : undefined
    return { fileId, preview, ...cardState }
  })
  const pendingCards = staging.pendingUploadClientIds.map((clientTmpId) => {
    const uploadState = uploadFilesByClientTmpId[clientTmpId]
    return { clientTmpId, ...getMedSummaryAssetFileCardState({ uploadState }) }
  })

  const hasBlockingDisposition = stagedCards.some((card) => card.preview && dispositionDisplay(card.preview)?.blocking)
  const hasUnreadyStaged = stagedCards.some((card) => !card.ready && !card.preview)
  const hasPendingUploads = pendingCards.length > 0
  const totalStaged = stagedUserFileIds.length + pendingCards.length
  const readyStagedCount = stagedCards.filter((card) => card.ready).length
  // Nothing to do if every staged file is already in the summary — block the no-op submit.
  const allStagedAlreadyIncluded =
    stagedCards.length > 0 && !hasPendingUploads && stagedCards.every((card) => card.preview?.disposition === FileAddDisposition.ALREADY_INCLUDED)

  const submitDisabled =
    stagedUserFileIds.length === 0 ||
    submitPending ||
    isUpdateActive ||
    hasBlockingDisposition ||
    hasUnreadyStaged ||
    hasPendingUploads ||
    allStagedAlreadyIncluded

  const submitDisabledHint = (() => {
    if (stagedUserFileIds.length === 0 && !hasPendingUploads) return null
    if (isUpdateActive) return 'An update is already in progress.'
    if (hasPendingUploads || hasUnreadyStaged) return 'Waiting for uploads to finish processing.'
    if (allStagedAlreadyIncluded) return 'All selected files are already in the billing summary.'
    if (hasBlockingDisposition) return 'Remove files that are unavailable, unsupported, or still processing.'
    return null
  })()

  const handleSubmit = useCallback(() => {
    if (submitDisabled || !activeMatterId) return
    submittedRef.current = true
    if (asset) {
      trackMedSummaryEvent(AnalyticsEvent.MedSummaryAssetUpdateClicked, { medSummaryAssetId: asset.id, medSummarySelectedFilesCount: stagedUserFileIds.length })
      dispatch(MedSummaryAssetActions.applyUpdate({ assetId: asset.id, addUserFileIds: stagedUserFileIds }))
    } else {
      trackMedSummaryEvent(AnalyticsEvent.MedSummaryAssetGenerateClicked, { matterId: activeMatterId, medSummarySelectedFilesCount: stagedUserFileIds.length })
      dispatch(MedSummaryAssetActions.initializeSummary({ matterId: activeMatterId, userFileIds: stagedUserFileIds }))
    }
  }, [submitDisabled, activeMatterId, asset, dispatch, stagedUserFileIds])

  const handleClearAll = useCallback(() => {
    staging.pendingUploadClientIds.forEach((clientTmpId) => upload.cancelUpload(clientTmpId))
    if (activeMatterId) dispatch(MedSummaryAssetActions.resetAddFilesStaging({ matterId: activeMatterId }))
  }, [staging.pendingUploadClientIds, upload, activeMatterId, dispatch])

  const dropzoneRootProps = upload.getRootProps()

  return (
    <div className="grow flex flex-col w-full h-full overflow-hidden">
      <MatterBreadcrumbsMedSummaryAsset subPageCrumb="Add Files" />

      <div className="flex-1 min-h-0 overflow-hidden flex flex-col items-center p-4 lg:p-6" {...dropzoneRootProps}>
        <input {...upload.getInputProps()} />
        <input
          ref={upload.fileInputRef}
          type="file"
          accept="application/pdf,.pdf,.docx,image/png,image/jpeg,image/gif,image/webp,image/bmp,image/tiff"
          multiple
          hidden
          onChange={upload.onFilesPicked}
        />

        <div className="w-full max-w-[946px] flex-1 min-h-0 flex flex-col bg-white border border-brand-neutral-200 rounded-lg overflow-hidden">
          {/* Header */}
          <div className="flex flex-col gap-3 px-6 pt-6 pb-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
            <div className="flex flex-col gap-1">
              <h1 className="font-rethink-sans text-lg font-semibold leading-7 text-brand-neutral-950">Add Files</h1>
              <p className="text-base font-normal leading-6 tracking-[-0.32px] text-brand-neutral-950">
                Upload or select an entire folder of medical bills, no splitting or batching required.
              </p>
            </div>
            <div className="flex items-center gap-3.5 shrink-0">
              <Button variant="outlined" color="primary" onClick={upload.open} disabled={upload.disabled}>
                Upload
              </Button>
              <Button variant="filled" color="primary" onClick={() => setIsMatterFilesDialogOpen(true)}>
                Matter Files
              </Button>
            </div>
          </div>

          {/* Body: included drawer, then dropzone when empty or the staged list */}
          <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 flex flex-col gap-2">
            {asset && asset.total_files > 0 && <IncludedInSummaryDrawer assetId={asset.id} totalFiles={asset.total_files} />}
            {totalStaged === 0 ? (
              <button
                type="button"
                onClick={upload.open}
                className={`w-full flex-1 min-h-[320px] rounded-lg flex flex-col items-center justify-center gap-3.5 px-4 pt-4 pb-9 transition-colors ${
                  upload.isDragActive ? 'bg-brand-50 outline-2 outline-dashed outline-brand-500' : ''
                }`}
              >
                <Upload className="w-6 h-6 text-brand-neutral-600" />
                <div className="flex flex-col items-center gap-2 text-center">
                  <p className="font-rethink-sans text-[15px] font-semibold leading-[25px] text-brand-neutral-600">Drag &amp; Drop to Upload or</p>
                  {/* Visual button (the whole dropzone is the real <button>, so this stays a styled span to avoid nested buttons) */}
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-md border border-brand-neutral-400 bg-brand-neutral-200 text-sm font-semibold tracking-[-0.15px] text-brand-neutral-800">
                    Choose Files
                  </span>
                </div>
              </button>
            ) : (
              <div className="flex flex-col gap-3 pt-2">
                <div className="flex flex-col gap-2 pb-4 pt-2">
                  <div className="flex items-center gap-2">
                    <p className="flex-1 font-rethink-sans text-base font-semibold leading-6 text-brand-neutral-950">Documents</p>
                    <p className="text-xs font-medium text-brand-neutral-600">
                      {readyStagedCount} of {totalStaged}
                    </p>
                  </div>
                  <div className="h-0.5 w-full bg-brand-neutral-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-brand-500 transition-all"
                      style={{ width: `${totalStaged ? Math.round((readyStagedCount / totalStaged) * 100) : 0}%` }}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-3">
                  {stagedCards.map((card) => (
                    <StagedFileCard
                      key={card.fileId}
                      fileName={card.fileName}
                      status={card.status}
                      disposition={dispositionDisplay(card.preview)}
                      duplicateOf={card.preview?.possible_duplicate_of?.display_name}
                      errorMessage={card.errorMessage || undefined}
                      onRemove={() => dispatch(MedSummaryAssetActions.unstageUserFileId({ fileId: card.fileId }))}
                      onOpen={
                        card.ready
                          ? () =>
                              openMedSummarySource({ provider: MedSummarySourceProvider.USER_FILES, source_id: card.fileId, display_name: card.fileName })
                          : undefined
                      }
                    />
                  ))}
                  {pendingCards.map((card) => (
                    <MedSummaryFileCard
                      key={card.clientTmpId}
                      fileName={card.fileName}
                      status={card.status}
                      errorMessage={card.errorMessage || undefined}
                      onRemove={() => {
                        upload.cancelUpload(card.clientTmpId)
                        dispatch(MedSummaryAssetActions.removePendingUserFileUpload(card.clientTmpId))
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer CTA bar — wraps on narrow widths so the primary CTA never clips off-screen */}
          <div className="border-t pl-4 pr-6 py-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-1">
                <Popover.Root>
                  <Popover.Trigger asChild>
                    <button
                      type="button"
                      className="flex items-center gap-1 whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium text-brand-neutral-600 hover:text-brand-neutral-800 transition-colors"
                    >
                      <InfoCircle className="w-3.5 h-3.5 shrink-0" />
                      Supported formats
                    </button>
                  </Popover.Trigger>
                  <Popover.Portal>
                    <Popover.Content
                      className="z-50 bg-white rounded-md shadow-[0_0.6px_25px_0_rgba(0,0,0,0.10),0_10px_10px_0_rgba(0,0,0,0.04)] p-5"
                      sideOffset={5}
                      align="start"
                    >
                      <p className="text-sm">
                        PDF, Word (.docx), and images
                        <br />
                        (PNG, JPEG, GIF, WEBP, BMP, TIFF).
                        <br />
                        Max file size 1GB.
                      </p>
                      <Popover.Arrow className="fill-white" width={16} height={8} />
                    </Popover.Content>
                  </Popover.Portal>
                </Popover.Root>
                <span className="h-4 w-px bg-brand-neutral-300 shrink-0" aria-hidden="true" />
                <Popover.Root>
                  <Popover.Trigger asChild>
                    <button
                      type="button"
                      className="flex items-center gap-1 whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium text-brand-neutral-600 hover:text-brand-neutral-800 transition-colors"
                    >
                      <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
                      HIPAA
                    </button>
                  </Popover.Trigger>
                  <Popover.Portal>
                    <Popover.Content
                      className="z-50 max-w-xs bg-white rounded-md shadow-[0_0.6px_25px_0_rgba(0,0,0,0.10),0_10px_10px_0_rgba(0,0,0,0.04)] p-5"
                      sideOffset={5}
                      align="start"
                    >
                      <p className="text-sm">HIPAA Certified. Your documents and data are secure, and never used to train {brandedAIName}.</p>
                      <Popover.Arrow className="fill-white" width={16} height={8} />
                    </Popover.Content>
                  </Popover.Portal>
                </Popover.Root>
              </div>
              {submitDisabledHint && <span className="text-xs text-brand-neutral-500">{submitDisabledHint}</span>}
              {/* First-generation failures surface on the summary blank-state page; the inline
                  footer error is only for update failures against an existing summary. */}
              {asset && submitError && <span className="text-xs text-red-700">{submitError}</span>}
            </div>
            <div className="flex items-center gap-3 shrink-0 ml-auto">
              <Button className="whitespace-nowrap" variant="outlined" color="destructive" onClick={handleClearAll} disabled={totalStaged === 0}>
                Clear All
              </Button>
              <Button className="whitespace-nowrap" variant="filled" color="primary" onClick={handleSubmit} disabled={submitDisabled}>
                {submitPending ? (
                  <span className="inline-flex items-center gap-2">
                    <CircularProgressContinuousSized size={14} thickness={4} />
                    {asset ? 'Updating…' : 'Generating…'}
                  </span>
                ) : asset ? (
                  'Update Summary'
                ) : (
                  'Generate Summary'
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <AddFilesMatterFilesDialog
        open={isMatterFilesDialogOpen}
        matterId={activeMatterId}
        initialSelectedIds={stagedUserFileIds}
        onClose={() => setIsMatterFilesDialogOpen(false)}
        onApply={(userFileIds) => dispatch(MedSummaryAssetActions.setStagedUserFileIds({ fileIds: userFileIds }))}
      />

      <ConflictModal {...upload.conflictDialog} operation="upload" />
    </div>
  )
}
